import React from "react";
import { Link } from "react-router-dom";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { getModuleFirstTabPath } from "src/framework/openModule.ts";
import { type UserResource, UserTypeEnum } from "@exonn/js-sdk";
import { useUser } from "src/auth/auth.ts";
import { getModules } from "src/system/getModules.ts";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { getModule } from "src/system/getModule.ts";
import { ResourceEnum } from "src/constants/constants.ts";

const BasicConfigurationTab = () => {
  const { data: user } = useUser();
  if (user) {
    return <BasicConfigurationTabLoggedIn user={user} />;
  }
  return null;
};

const BasicConfigurationTabLoggedIn = ({ user }: { user: UserResource }) => {
  const { t } = useTranslation(TranslationNamespaceEnum.Common);
  const clientConfig = useClientConfig();
  const availableModules = getModules(clientConfig);
  const administrationModules = [
    getModule(ResourceEnum.User),
    getModule(ResourceEnum.Country),
    // getModule(ResourceEnum.Role),
    getModule(ResourceEnum.Condition),
    getModule(ResourceEnum.PaymentMethod),
    getModule(ResourceEnum.Language),
    getModule(ResourceEnum.CustomerGroup),
    getModule(ResourceEnum.DeliveryService),
    getModule(ResourceEnum.DeliveryMethod),
    getModule(ResourceEnum.Currency),
    getModule(ResourceEnum.Owner),
    getModule(ResourceEnum.OwnerProfile),
    getModule(ResourceEnum.Warehouse),
    getModule(ResourceEnum.ProcessingTime),
    getModule(ResourceEnum.Marketplace),
    getModule(ResourceEnum.Unit),
    getModule(ResourceEnum.Vat),
    getModule(ResourceEnum.ConfigNumber),
    getModule(ResourceEnum.DocumentTemplate),
    getModule(ResourceEnum.EmailTemplate),
    ...(user.user_type === UserTypeEnum.Developer
      ? [getModule(ResourceEnum.IntegrationToken)]
      : []),
    getModule(ResourceEnum.AccountingTemplate),
  ];

  const filteredModules = administrationModules.filter((adminModule) => {
    return availableModules.some(
      (availableModule) => availableModule.id === adminModule.id,
    );
  });
  return (
    <div css={contentContainer}>
      <div css={menuContainer}>
        {filteredModules.map((module) => {
          return (
            <Link
              css={itemContainer}
              to={getModuleFirstTabPath({
                resource: module.id,
                user,
              })}
              key={module.id}
            >
              <div css={iconContainer}>{module.bigIcon}</div>
              <div>{module.name(t)}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const menuContainer = css`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
`;

const itemContainer = (theme: ExactTheme) => css`
  background: ${theme.colorsV2.grey[25]};
  border: 1px solid ${theme.colorsV2.grey[100]};
  color: ${theme.colorsV2.grey[800]};
  text-decoration: unset;
  width: 258px;
  height: 48px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  :hover {
    background-color: ${theme.colorsV2.grey[50]};
  }
  :active {
    background-color: ${theme.colorsV2.grey[100]};
  }
`;

const iconContainer = css`
  margin: 0 10px 0 15px;
  font-size: 20px;
  display: flex;
  align-items: center;
`;

const contentContainer = (theme: ExactTheme) => css`
  background-color: ${theme.colorsV2.white};
  width: 100%;
  height: 100%;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top-right-radius: 6px;
  padding: 15px;
`;

export default BasicConfigurationTab;
